import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where, updateDoc, doc, serverTimestamp, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { openInGoogleMaps, formatCoordinates } from '../utils/locationTracker';
import './SupervisorOutletManager.css';

const EQUIPMENT_TYPES = [
  { value: 'all',           label: 'All Equipment' },
  { value: 'keg_pump',      label: '☕ Keg Pump'      },
  { value: 'draft_machine', label: '🍺 Draft Machine'  },
  { value: 'both',          label: '🔧 Both'           },
  { value: 'none',          label: '— None'            },
];

function SupervisorOutletManager({ user, onBack }) {
  const [outlets, setOutlets]         = useState([]);
  const [services, setServices]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null);
  const [view, setView]               = useState('list');  // 'list' | 'detail'
  const [searchTerm, setSearchTerm]   = useState('');
  const [filterArea, setFilterArea]   = useState('all');
  const [filterEquip, setFilterEquip] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  // ✅ FIXED: outlet service history is now fetched on demand, scoped to
  // one outlet, only when its detail view is opened — not bulk-loaded
  // for every outlet on every page load. Falls back to matching by
  // outletName for outlets registered before outletId was attached to
  useEffect(() => {
    if (!selected) { setServices([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'serviceRecords'),
            where('outletName', '==', selected.outletName),
            orderBy('createdAt', 'desc'),
            limit(10)
          )
        );
        if (!cancelled) setServices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading outlet service history:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Outlets only — service history is loaded separately, on demand,
      // per the effect above. Previously this also downloaded the ENTIRE
      // serviceRecords collection just to support a "last 10 services"
      // view for whichever single outlet a supervisor happened to open.
      const outletSnap = await getDocs(query(collection(db, 'outlets'), orderBy('createdAt', 'desc')));
      setOutlets(outletSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('❌ Load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (outlet) => {
    if (!window.confirm(`Deactivate "${outlet.outletName}"?`)) return;
    try {
      await updateDoc(doc(db, 'outlets', outlet.id), { status: 'inactive', updatedAt: serverTimestamp() });
      await loadAll();
      setView('list');
      showToast('⚠️ Outlet deactivated.');
    } catch (e) {
      console.error('❌ Deactivate error:', e);
    }
  };

  const handleReactivate = async (outlet) => {
    try {
      await updateDoc(doc(db, 'outlets', outlet.id), { status: 'active', updatedAt: serverTimestamp() });
      await loadAll();
      showToast('✅ Outlet reactivated.');
    } catch (e) {
      console.error('❌ Reactivate error:', e);
    }
  };

  // All unique areas for filter
  const areas = ['all', ...new Set(outlets.map(o => o.area).filter(Boolean))].sort();

  const filtered = outlets.filter(o => {
    if (!showInactive && o.status === 'inactive') return false;
    if (filterArea !== 'all' && o.area !== filterArea) return false;
    if (filterEquip !== 'all' && o.equipmentType !== filterEquip) return false;
    const term = searchTerm.toLowerCase();
    return !term ||
      o.outletName?.toLowerCase().includes(term) ||
      o.area?.toLowerCase().includes(term) ||
      o.contactPerson?.toLowerCase().includes(term) ||
      o.contactPhone?.includes(term);
  });

  // ✅ REMOVED: getOutletServices(). The `services` state is now already
  // fetched scoped to whichever outlet is selected (see the effect
  // above), so there's nothing left to filter.

  const getStats = () => ({
    total:    outlets.length,
    active:   outlets.filter(o => o.status === 'active').length,
    inactive: outlets.filter(o => o.status === 'inactive').length,
    kegPump:  outlets.filter(o => o.equipmentType === 'keg_pump' || o.equipmentType === 'both').length,
    draft:    outlets.filter(o => o.equipmentType === 'draft_machine' || o.equipmentType === 'both').length,
  });

  const exportCSV = () => {
    let csv = `Outlet Directory Export\nGenerated: ${new Date().toLocaleString('en-KE')}\n\n`;
    csv += `Name,Area,Address,Contact Person,Phone,Alternate Phone,Equipment,Serial Numbers,Status,Registered By,Services Done,GPS\n`;
    filtered.forEach(o => {
      const gps = o.location
        ? `${o.location.latitude.toFixed(6)}, ${o.location.longitude.toFixed(6)}`
        : 'N/A';
      csv += `"${o.outletName}","${o.area}","${o.address||''}","${o.contactPerson||''}","${o.contactPhone||''}","${o.alternatePhone||''}","${o.equipmentType||''}","${o.serialNumbers||''}","${o.status}","${o.registeredByName||''}","${o.serviceCount||0}","${gps}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `outlets_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
  };

  const getEqIcon = (type) =>
    ({ keg_pump: '☕', draft_machine: '🍺', both: '🔧', none: '—' }[type] || '—');

  const showToast = (msg) => {
    document.querySelectorAll('.toast-notification').forEach(t => t.remove());
    const t = document.createElement('div');
    t.className = 'toast-notification';
    t.innerHTML = `<div class="toast-icon">🏪</div><div class="toast-message">${msg}</div>`;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3000);
  };

  const stats = getStats();

  // ── DETAIL VIEW ──────────────────────────────────────────
  if (view === 'detail' && selected) {
    const outletServices = services;
    return (
      <div className="som-page">
        <div className="som-header">
          <button onClick={() => setView('list')} className="back-button">← Back</button>
          <h1>🏪 {selected.outletName}</h1>
          <div className="detail-header-actions">
            {selected.status === 'active'
              ? <button className="deactivate-btn" onClick={() => handleDeactivate(selected)}>🚫 Deactivate</button>
              : <button className="reactivate-btn" onClick={() => handleReactivate(selected)}>✅ Reactivate</button>
            }
          </div>
        </div>

        <div className="som-detail-card">
          {/* Hero */}
          <div className="som-detail-hero">
            <div className="som-hero-icon">{getEqIcon(selected.equipmentType)}</div>
            <div>
              <h2>{selected.outletName}</h2>
              <p className="som-area">📍 {selected.area}{selected.address ? ` · ${selected.address}` : ''}</p>
              <span className={`som-status-pill ${selected.status}`}>
                {selected.status === 'active' ? '🟢 Active' : '🔴 Inactive'}
              </span>
            </div>
          </div>

          {/* Info grid */}
          <div className="som-info-grid">
            {[
              ['Contact Person',  selected.contactPerson || '—'],
              ['Primary Phone',   selected.contactPhone, true],
              ['Alt Phone',       selected.alternatePhone, selected.alternatePhone],
              ['Equipment',       `${getEqIcon(selected.equipmentType)} ${selected.equipmentType?.replace('_',' ')}`],
              ['Serial Numbers',  selected.serialNumbers || '—'],
              ['Registered By',   selected.registeredByName || '—'],
              ['Registered On',   formatDate(selected.createdAt)],
              ['Services Done',   selected.serviceCount || 0],
            ].filter(([,v,show]) => show !== false).map(([label, value, isPhone]) => (
              <div className="som-info-item" key={label}>
                <span className="som-info-label">{label}</span>
                {isPhone && value !== '—'
                  ? <a href={`tel:${value}`} className="som-info-value som-phone">📞 {value}</a>
                  : <span className="som-info-value">{value}</span>
                }
              </div>
            ))}
          </div>

          {selected.notes && (
            <div className="som-notes-block">
              <div className="som-info-label">Notes</div>
              <p>{selected.notes}</p>
            </div>
          )}

          {selected.location && (
            <div className="som-gps-block">
              <div className="som-info-label">GPS Location</div>
              <div className="som-gps-row">
                <span className="som-gps-text">
                  {formatCoordinates(selected.location.latitude, selected.location.longitude, selected.location.accuracy)}
                </span>
                <button className="som-map-btn"
                  onClick={() => openInGoogleMaps(selected.location.latitude, selected.location.longitude, selected.outletName)}>
                  🗺️ Open Maps
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Service History */}
        <div className="som-history-card">
          <div className="som-history-header">
            <h3>Service History</h3>
            <span className="som-history-count">{outletServices.length} records</span>
          </div>

          {outletServices.length === 0 ? (
            <div className="som-empty-history">No service records found for this outlet</div>
          ) : (
            <div className="som-history-list">
              {outletServices.map(svc => (
                <div key={svc.id} className="som-history-item">
                  <div className="som-history-icon">
                    {svc.serviceType === 'emergency' ? '🚨' : '🔧'}
                  </div>
                  <div className="som-history-body">
                    <div className="som-history-top">
                      <span className="som-history-tech">{svc.technicianName}</span>
                      <span className={`som-history-status ${svc.status}`}>{svc.status?.replace('_',' ')}</span>
                    </div>
                    <div className="som-history-meta">
                      {formatDate(svc.createdAt)} {formatTime(svc.createdAt)}
                      {svc.faultDescription && ` · ${svc.faultDescription.slice(0,50)}…`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────
  return (
    <div className="som-page">
      <div className="som-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="som-header-text">
          <h1>🏪 Outlet Manager</h1>
          <p>{stats.active} active · {stats.total} total</p>
        </div>
        <button className="som-export-btn" onClick={exportCSV} disabled={filtered.length === 0}>
          ⬇️ Export
        </button>
      </div>

      {/* Stats */}
      <div className="som-stats">
        {[
          { label: 'Total',    val: stats.total,    color: 'var(--sv-ink)'    },
          { label: 'Active',   val: stats.active,   color: 'var(--sv-success)' },
          { label: 'Keg Pump', val: stats.kegPump,  color: '#92722E'          },
          { label: 'Draft',    val: stats.draft,    color: 'var(--sv-maroon)' },
        ].map(s => (
          <div className="som-stat" key={s.label}>
            <span className="som-stat-val" style={{ color: s.color }}>{s.val}</span>
            <span className="som-stat-lbl">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="som-filters">
        <div className="search-bar">
          <span>🔍</span>
          <input className="search-input" placeholder="Search name, area, contact..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          {searchTerm && <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>}
        </div>

        <div className="som-filter-row">
          <select className="som-select" value={filterArea} onChange={e => setFilterArea(e.target.value)}>
            {areas.map(a => <option key={a} value={a}>{a === 'all' ? 'All Areas' : a}</option>)}
          </select>
          <select className="som-select" value={filterEquip} onChange={e => setFilterEquip(e.target.value)}>
            {EQUIPMENT_TYPES.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <label className="som-toggle">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            <span>Show Inactive</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner"></div><p>Loading outlets...</p></div>
      ) : filtered.length === 0 ? (
        <div className="som-empty">
          <div className="empty-icon">📭</div>
          <p>No outlets match your filters</p>
        </div>
      ) : (
        <div className="som-list">
          {filtered.map(outlet => (
            <div key={outlet.id}
              className={`som-outlet-row ${outlet.status === 'inactive' ? 'som-inactive' : ''}`}
              onClick={() => { setSelected(outlet); setView('detail'); }}>
              <div className="som-row-icon">{getEqIcon(outlet.equipmentType)}</div>
              <div className="som-row-body">
                <div className="som-row-name">{outlet.outletName}
                  {outlet.status === 'inactive' && <span className="som-inactive-badge">Inactive</span>}
                </div>
                <div className="som-row-meta">
                  📍 {outlet.area}
                  {outlet.contactPerson ? ` · 👤 ${outlet.contactPerson}` : ''}
                  {` · ${outlet.serviceCount || 0} services`}
                </div>
              </div>
              <div className="som-row-right">
                <span className="som-row-eq">{outlet.equipmentType?.replace('_',' ')}</span>
                <span className="som-row-date">{formatDate(outlet.createdAt)}</span>
                <span className="som-chevron">›</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SupervisorOutletManager;
